import { buildHttpError } from '@/shared/libs/httpError';
import logger from '@/shared/libs/logger.client';
import { normalizeHeaders } from '@/shared/utils/authHelpers';

if (!process.env.NEXT_PUBLIC_API_BASE_URL) {
  throw new Error('NEXT_PUBLIC_API_BASE_URL is not defined in the environment');
}

/**
 * A lightweight HTTP client wrapper around `fetch` for public (non-authenticated) API calls.
 *
 * Features:
 * - Normalizes headers and ensures `Content-Type: application/json` is set by default.
 * - Does **not** attach an `Authorization` header or handle token refresh (unlike `httpClient`).
 * - Builds the request URL based on `NEXT_PUBLIC_API_BASE_URL`.
 * - Provides consistent error handling (parses JSON error body if possible, otherwise returns raw text).
 *
 * @template T - Expected response type.
 * @param {string} endpoint - The API endpoint relative to the base URL.
 * @param {RequestInit} [options={}] - Optional fetch options (method, headers, body, etc.).
 * @returns {Promise<T>} A promise that resolves to the parsed JSON response of type `T`.
 * @throws Will throw an error if the network request fails or if the response is not OK.
 */
export async function publicHttpClient<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers = normalizeHeaders(options.headers);
  headers['Content-Type'] = 'application/json';
  // Source de l'action pour le journal d'audit BE.
  headers['x-client'] = headers['x-client'] ?? 'web';

  // Chemin interne (`/api/...`) → on l'utilise tel quel pour passer par le proxy BFF
  // de l'app (même origine), qui relaie vers `BACKEND_API_BASE_URL`. Sinon on préfixe
  // par `NEXT_PUBLIC_API_BASE_URL`. Même logique que `httpClient` : sans cette branche,
  // les appels publics `/api/v1/public/...` (ex. onboarding) tapaient la mauvaise
  // origine/port et échouaient → « Lien indisponible » sur un token pourtant valide.
  // Normalise aussi les slashes pour éviter un `//` (redirection 308).
  const isInternal = endpoint.startsWith('/api/');
  const base = process.env.NEXT_PUBLIC_API_BASE_URL!.replace(/\/+$/, '');
  const path = endpoint.replace(/^\/+/, '');
  const url = isInternal ? endpoint : `${base}/${path}`;

  let response: Response;
  try {
    response = await fetch(url, {
      headers,
      ...options,
    });
  } catch (error) {
    logger.error('[publicHttpClient] Network error', { error });
    throw new Error('Impossible de contacter le serveur. Vérifiez votre connexion.');
  }

  if (!response.ok) {
    /*
      **Le même constructeur que le client authentifié** (#2228). Cette copie jetait le `code`
      machine du serveur — invisible jusqu'à ce qu'un refus ait besoin d'être distingué d'un autre.
      `FEATURE_NOT_ENABLED` arrive précisément par ici : le portail apporteur et le formulaire de
      contact d'un tenant n'ont pas de session (suivora-api#1718).
    */
    throw buildHttpError(response.status, await response.text());
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentLength = response.headers.get('content-length');
  if (contentLength === '0') {
    return undefined as T;
  }

  const rawBody = await response.text();
  if (!rawBody) {
    return undefined as T;
  }

  return JSON.parse(rawBody) as T;
}
